import { Component, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CustomMessageService } from '@shared/components/custom-message/custom-message.service';
import { GaPdfService } from '@app/shared/pdf/ga-pdf.service';
import { LogisticsCatalogs, TransfersService, errMsg } from '../logistics.service';
import { buildTransferDoc, transferPdfData } from './transfer-pdf';

const LAST_TO_KEY = 'transfers.dhlTo';

/**
 * Paso 5: solicitud de recolección a DHL. El correo sale prellenado; mesa de
 * control lo revisa, lo edita y lo envía. Va con el PDF del traspaso (en lugar
 * del Excel). El servidor solo acepta destinatarios de los dominios
 * permitidos y siempre copia a mesa de control.
 */
@Component({
  selector: 'app-dhl-mail-dialog',
  standalone: true,
  imports: [FormsModule, MatDialogModule, MatFormFieldModule, MatInputModule, MatButtonModule, MatIconModule,
    MatSlideToggleModule, MatProgressSpinnerModule],
  styleUrl: '../logistics-dialog.scss',
  styles: [`.wrap { display: grid; gap: 4px; min-width: min(640px, 86vw); } textarea { font-family: inherit; line-height: 1.4; }`],
  template: `
<h2 mat-dialog-title>Correo de recolección a DHL · {{ t.folio }}</h2>
<mat-dialog-content>
  <div class="wrap">
    @if (t.dhlMail?.sentOn) {
      <div class="hint-box hint-box--warn"><mat-icon>history</mat-icon>
        <span>Ya se envió un correo para este traspaso ({{ t.dhlMail.to?.join(', ') }}). Si lo mandas otra vez, DHL recibe otra solicitud.</span></div>
    }
    <mat-form-field appearance="outline">
      <mat-label>Para</mat-label>
      <input matInput [(ngModel)]="to" placeholder="recolecciones@dhl.com" autocomplete="off">
      <mat-hint>Separa varios correos con coma.</mat-hint>
    </mat-form-field>
    <mat-form-field appearance="outline">
      <mat-label>CC</mat-label>
      <input matInput [(ngModel)]="cc" autocomplete="off">
      <mat-hint>Mesa de control va en copia siempre.</mat-hint>
    </mat-form-field>
    <mat-form-field appearance="outline">
      <mat-label>Asunto</mat-label>
      <input matInput [(ngModel)]="subject" maxlength="200">
    </mat-form-field>
    <mat-form-field appearance="outline">
      <mat-label>Mensaje</mat-label>
      <textarea matInput rows="12" [(ngModel)]="body" maxlength="5000"></textarea>
    </mat-form-field>
    <mat-slide-toggle color="primary" [(ngModel)]="attach">Adjuntar el PDF del traspaso (Traspaso-{{ t.folio }}.pdf)</mat-slide-toggle>
  </div>
</mat-dialog-content>
<mat-dialog-actions align="end">
  <button mat-button (click)="ref.close(false)" [disabled]="sending()">Cancelar</button>
  <button mat-stroked-button (click)="previewPdf()" [disabled]="sending()"><mat-icon>picture_as_pdf</mat-icon> Ver PDF</button>
  <button mat-flat-button color="primary" (click)="send()" [disabled]="sending() || !to.trim()">
    @if (sending()) {<mat-spinner diameter="18"></mat-spinner>} @else {<mat-icon>send</mat-icon>}
    Enviar a DHL
  </button>
</mat-dialog-actions>
  `,
})
export class DhlMailDialogComponent {
  private readonly data = inject<{ transfer: any; requestFolio?: string }>(MAT_DIALOG_DATA);
  readonly t = this.data.transfer;
  readonly ref = inject(MatDialogRef<DhlMailDialogComponent>);
  readonly cat = inject(LogisticsCatalogs);
  private readonly service = inject(TransfersService);
  private readonly pdf = inject(GaPdfService);
  private readonly msg = inject(CustomMessageService);

  readonly sending = signal(false);
  to = this.t.dhlMail?.to?.join(', ') || this.lastTo();
  cc = '';
  attach = true;
  subject = `Solicitud de recolección · Traspaso ${this.t.folio} · ${this.cat.placeName(this.t.originPlace)} → ${this.cat.placeName(this.t.destinationPlace)}`;
  body = this.draft();

  private lastTo(): string { try { return localStorage.getItem(LAST_TO_KEY) ?? ''; } catch { return ''; } }

  private draft(): string {
    const n = (this.t.items ?? []).length;
    const oAddr = this.cat.placeAddress(this.t.originPlace);
    const dAddr = this.cat.placeAddress(this.t.destinationPlace);
    return [
      'Buen día,',
      '',
      'Les solicitamos la recolección del siguiente material:',
      '',
      `Folio de traspaso: ${this.t.folio}`,
      '',
      `Recolectar en: ${this.cat.warehouseLabelById(this.t.originWarehouse)}`,
      oAddr ? `Dirección: ${oAddr}` : 'Dirección: ',
      'Contacto: ',
      '',
      `Entregar en: ${this.cat.warehouseLabelById(this.t.destinationWarehouse)}`,
      dAddr ? `Dirección: ${dAddr}` : 'Dirección: ',
      'Contacto: ',
      '',
      `Piezas: ${n} (detalle en el PDF adjunto).`,
      '',
      'Favor de confirmar la guía de envío.',
      '',
      'Saludos.',
    ].join('\n');
  }

  private split(s: string): string[] { return s.split(/[,;\s]+/).map(x => x.trim()).filter(Boolean); }

  private doc(): any { return buildTransferDoc(transferPdfData(this.t, this.cat, this.data.requestFolio ?? '')); }

  async previewPdf(): Promise<void> {
    const url = await this.pdf.preview(this.doc());
    window.open(url, '_blank');
  }

  async send(): Promise<void> {
    this.sending.set(true);
    try {
      const pdfBase64 = this.attach ? await this.pdf.base64(this.doc()) : undefined;
      const to = this.split(this.to);
      await this.service.dhlMail({ id: this.t.id, to, cc: this.split(this.cc), subject: this.subject, body: this.body,
        pdfBase64, fileName: `Traspaso-${this.t.folio}.pdf` });
      try { localStorage.setItem(LAST_TO_KEY, to.join(', ')); } catch { /* sin storage */ }
      await this.msg.open({ title: '¡Enviado!', type: 'success', duration: 2600,
        message: `Se pidió la recolección del traspaso <b>${this.t.folio}</b>. Cuando DHL dé la guía, captúrala en el traspaso.` });
      this.ref.close(true);
    } catch (err) {
      await this.msg.open({ title: 'No se envió', type: 'error', message: errMsg(err, 'No se pudo enviar el correo.'), dismissBtn: true });
    } finally {
      this.sending.set(false);
    }
  }
}
